/**
 * Sanitize admin-authored rich text (TipTap/RichTextEditor HTML) for rendering
 * on the storefront and in checkout.
 *
 * ALLOWLIST, NOT DENYLIST. The previous version tried to strip the bad things
 * with regexes and six of ten standard payloads walked straight through it:
 *
 *   <img/onerror=alert(1) src=x>   a "/" is not \s, so the ` on\w+=` rule missed it
 *   <svg/onload=alert(1)>          same
 *   <script src=//evil.tld/x.js    unclosed, so neither script rule matched
 *   <a href="javascript&#58;...">  entity-encoded colon, decoded later by the browser
 *   <a href="java&Tab;script:..."> entity-encoded tab inside the scheme
 *   <a href="data:text/html,...">  only javascript: was blocked
 *
 * A denylist has to anticipate every encoding; an allowlist only has to know
 * what is permitted. Every tag is re-emitted from scratch carrying ONLY
 * approved attributes, so an attribute like `onerror` cannot survive however it
 * is spelled or separated — it is never copied across in the first place.
 *
 * Deliberately dependency-free: isomorphic-dompurify would pull jsdom into the
 * server bundle, and this renders inside a client component that Next also
 * server-renders, on pages that carry an on-sale.
 */

/** Tags a description may contain. Anything else is dropped, content kept. */
const ALLOWED_TAGS = new Set([
  'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'del',
  'ul', 'ol', 'li', 'blockquote', 'code', 'pre',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'a', 'img', 'span', 'div', 'hr', 'sub', 'sup', 'small',
  // Legacy branding footers/headers are run through this too and some use tables.
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th',
])

/** Tags whose CONTENT is thrown away too, not just the tag. */
const DROP_CONTENT = new Set(['script', 'style', 'iframe', 'object', 'embed', 'form', 'svg', 'math', 'template', 'noscript'])

/** Per-tag attribute allowlist. Everything not named here is discarded. */
const ALLOWED_ATTRS: Record<string, Set<string>> = {
  a: new Set(['href', 'title', 'target', 'rel']),
  img: new Set(['src', 'alt', 'title', 'width', 'height']),
  td: new Set(['colspan', 'rowspan']),
  th: new Set(['colspan', 'rowspan']),
}

/** Schemes a href/src may use. Relative URLs are allowed separately. */
const SAFE_SCHEMES = ['http:', 'https:', 'mailto:', 'tel:']

/**
 * Decode the encodings a browser would decode before acting on a URL, so the
 * scheme check sees what the browser will see. `javascript&#58;alert(1)` and
 * `java&Tab;script:alert(1)` both become `javascript:alert(1)` here and are
 * then rejected; checking the raw string let them through.
 */
function decodeEntities(value: string): string {
  return value
    .replace(/&#x([0-9a-f]+);?/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
    .replace(/&#(\d+);?/g, (_, dec) => String.fromCharCode(parseInt(dec, 10)))
    .replace(/&Tab;/gi, '\t')
    .replace(/&NewLine;/gi, '\n')
    .replace(/&colon;/gi, ':')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&apos;/gi, "'")
    .replace(/&amp;/gi, '&')
}

/** True when this URL is safe to put in an href/src. */
function isSafeUrl(raw: string): boolean {
  // Strip every whitespace and control character: browsers ignore them inside
  // a scheme, so "java\nscript:" and "java\tscript:" both execute.
  const decoded = decodeEntities(raw).replace(/[\u0000- \u007f-\u009f]/g, '')
  if (!decoded) return false
  const lower = decoded.toLowerCase()
  // A scheme is everything up to the first colon, if it comes before any / ? #
  const colon = lower.indexOf(':')
  if (colon === -1) return true // relative: /foo, foo.png, #anchor
  const firstSep = Math.min(
    ...['/', '?', '#'].map(c => { const i = lower.indexOf(c); return i === -1 ? Infinity : i }),
  )
  if (colon > firstSep) return true // e.g. "/a/b:c" — the colon is in the path
  return SAFE_SCHEMES.includes(lower.slice(0, colon + 1))
}

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

/**
 * Pull attributes out of a tag's interior. Handles quoted, single-quoted and
 * bare values, and — crucially — treats "/" and any whitespace as separators,
 * which is the hole the old handler regex had.
 */
function parseAttrs(inner: string): Array<[string, string]> {
  const out: Array<[string, string]> = []
  const re = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)\s*(?:=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>`]+)))?/g
  let m: RegExpExecArray | null
  while ((m = re.exec(inner)) !== null) {
    const name = m[1].toLowerCase()
    const value = m[2] ?? m[3] ?? m[4] ?? ''
    out.push([name, value])
  }
  return out
}

export function sanitizeRichText(html: string | null | undefined): string {
  if (!html) return ''

  // 1. Remove the tags whose content must go with them, including unclosed
  //    ones: "<script src=x" with no ">" is cut to the end of the input rather
  //    than left in place, which is how `<script src=//evil/x.js` survived.
  let working = String(html)
  for (const tag of DROP_CONTENT) {
    working = working
      .replace(new RegExp(`<${tag}\\b[\\s\\S]*?</${tag}\\s*>`, 'gi'), '')
      .replace(new RegExp(`<${tag}\\b[^>]*>`, 'gi'), '')
      .replace(new RegExp(`<${tag}\\b[\\s\\S]*$`, 'gi'), '')
  }
  // Comments can hide markup from a naive pass; conditional comments execute.
  working = working.replace(/<!--[\s\S]*?(?:-->|$)/g, '')

  // 2. Re-emit every remaining tag from scratch. Nothing is copied through.
  return working.replace(/<\/?([a-zA-Z][a-zA-Z0-9]*)((?:[^>"']|"[^"]*"|'[^']*')*)>?/g, (match, rawName: string, rawInner: string) => {
    const name = rawName.toLowerCase()
    if (!ALLOWED_TAGS.has(name)) return ''
    if (match.startsWith('</')) return `</${name}>`

    const permitted = ALLOWED_ATTRS[name]
    const kept: string[] = []
    if (permitted) {
      for (const [attr, value] of parseAttrs(rawInner || '')) {
        if (!permitted.has(attr)) continue
        if ((attr === 'href' || attr === 'src') && !isSafeUrl(value)) continue
        // Anchors that open a new tab get noopener, or the opened page can
        // reach back through window.opener.
        kept.push(`${attr}="${escapeAttr(value)}"`)
      }
      if (name === 'a' && kept.some(a => a.startsWith('target='))) {
        kept.push('rel="noopener noreferrer"')
      }
    }
    const selfClosing = name === 'br' || name === 'img' || name === 'hr'
    return `<${name}${kept.length ? ' ' + kept.join(' ') : ''}${selfClosing ? ' /' : ''}>`
  })
}

/** Plain-text form for tooltips/clamped one-liners (tags stripped). */
export function richTextToPlain(html: string | null | undefined): string {
  if (!html) return ''
  return html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|div|li|h[1-6])>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/\s+/g, ' ')
    .trim()
}
